import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { c, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { FormRadioSelect } from "../../../forms/FormRadioSelect";
import { FinancingPlan } from "../../../models/financing";
import { IMonths, isoAPR, isoMonths } from "../../../models/nominals";
import { format } from "../../../utils/format";
import { getDinero, getMonthlyPrice } from "../../../utils/money";
import {
    IFinancingPayment,
    IPayment,
    IReduxFormState,
} from "../reducers.interfaces";

const getMethodData = (
    form: IReduxFormState,
    methodKey: string,
): IFinancingPayment => {
    const data = form.payment_methods[methodKey];
    let wellsFargoData: IFinancingPayment | null = null;
    if (data.method_type === "financing") {
        wellsFargoData = data;
    }
    const baseFields: IFinancingPayment = {
        method_type: "financing",
        amount: "",
        pay_balance: false,
        new_financing_account: null,
        financing_account: "",
        financing_plan: null,
        has_agreed: false,
        has_esigned: false,
    };
    return {
        ...baseFields,
        ...wellsFargoData,
    };
};

const getTotal = (form: IReduxFormState, methodKey: string): string => {
    return form.payment_methods[methodKey].amount || "0.00";
};

const getPlanDescription = (plan: FinancingPlan): React.JSX.Element => {
    if (
        plan.term_months <= isoMonths.wrap(0) ||
        parseFloat(isoAPR.unwrap(plan.apr)) > 0
    ) {
        return <span>{t`Regular Terms Apply`}</span>;
    }
    return (
        <span className="heavy">{c("SelectPlanForm")
            .t`${plan.term_months} months`}</span>
    );
};

const getPlanExtraDescription = (plan: FinancingPlan): React.JSX.Element => {
    return (
        <span className="tiny">
            {t`Minimum Financing Amount:`}{" "}
            {format.money(plan.product_price_threshold)}
        </span>
    );
};

const getEstimatedPayments = (
    plan: FinancingPlan,
    total: string,
): React.JSX.Element => {
    if (
        plan.term_months <= isoMonths.wrap(0) ||
        parseFloat(isoAPR.unwrap(plan.apr)) > 0
    ) {
        return (
            <span>
                You can locate your rate on your monthly statement or by calling
                Wells Fargo at: <br />
                1-800-431-5921
            </span>
        );
    }
    const payment = getMonthlyPrice(getDinero(total), plan.term_months);
    return (
        <span>
            {richT(
                interpolate(
                    t`%(payment)s/month<sup>2</sup> for %(length)s months (total payments of %(total)s)`,
                    {
                        payment: `${format.money(payment)}`,
                        length: `${plan.term_months}`,
                        total: `${format.money(total)}`,
                    },
                ),
            )}
        </span>
    );
};

const buildTermHeader = (
    plans: readonly FinancingPlan[],
    total: string,
): React.JSX.Element | null => {
    const qualifies = plans.some(
        (plan) =>
            parseFloat(isoAPR.unwrap(plan.apr)) <= 0 &&
            parseFloat(plan.product_price_threshold) <= parseFloat(total),
    );
    if (!qualifies) {
        return null;
    }

    const aprInfo = interpolate(
        t`Your order of %s qualifies for a special interest rate of 0% APR with equal monthly payments.`,
        [format.money(getDinero(total))],
    );

    return (
        <>
            <h3 className="checkout-step__copy--bold">
                {t`Special Terms & APR Information`}
            </h3>
            <div className="form-options__financing-terms">{aprInfo}</div>
        </>
    );
};

export const SelectPlanForm = (
    props: Immutable<{
        methodKey: string;
        form: IReduxFormState;
        plans: readonly FinancingPlan[];
        grandTotal: string;
        setPaymentMethodFields: (
            methodKey: string,
            fields: Partial<IPayment>,
        ) => void;
        setPlanMonths: (months: IMonths) => void;
        disabled: boolean;
    }>,
): React.ReactNode => {
    React.useEffect(() => {
        props.setPlanMonths(props.plans[0].term_months);
    }, []);

    const onChange = (event: React.FormEvent<HTMLInputElement>) => {
        const planID = parseInt(event.currentTarget.value, 10);
        const plan = props.plans.find((x) => {
            return x.id === planID;
        });
        if (!plan) {
            return;
        }
        const planMonths = plan.term_months;
        props.setPlanMonths(planMonths);
        props.setPaymentMethodFields(props.methodKey, {
            financing_plan: planID,
        });
    };

    const formClass = classNames({
        "form-options-financing": true,
    });

    const total = getTotal(props.form, props.methodKey);
    const methodData = getMethodData(props.form, props.methodKey);

    const plans = props.plans.map((plan) => {
        return {
            value: `${plan.id}`,
            label: getPlanDescription(plan),
            helpText: getEstimatedPayments(plan, total),
            extraText: !props.disabled ? getPlanExtraDescription(plan) : "",
            disabled:
                props.disabled ||
                parseFloat(plan.product_price_threshold) > parseFloat(total),
        };
    });

    const termHeader = buildTermHeader(props.plans, total);

    return (
        <div className={formClass}>
            {termHeader}
            <div className="form-options__financing-terms--choices">
                <FormRadioSelect
                    name="financing_plan"
                    value={`${methodData.financing_plan}`}
                    onChange={onChange}
                    legend={t`Select term length:`}
                    choices={plans}
                />
            </div>
        </div>
    );
};
